import { useQuery } from "@tanstack/react-query";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Facebook, Linkedin, Twitter, Link2, Printer, MessageCircle, Volume2, Square, QrCode, Compass, CheckCircle2 } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import api from "@/lib/api";
import { MedicalBadge } from "@/components/MedicalBadge";
import { SafetyNotice } from "@/components/SafetyNotice";
import usePageTitle from "@/hooks/usePageTitle";

const GUIDE_SLUG = "keralas-golden-trio";
const GUIDE_TITLE = "Kerala's Golden Trio: A Guide to Backwaters, Beaches, and Hill Stations";
const GUIDE_IMAGE = "https://images.unsplash.com/photo-1609828913552-f9138ed9e42d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600";

function fmt(d) {
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return d;
  }
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: a, isLoading, isError } = useQuery({
    queryKey: ["article", slug],
    queryFn: () => api.get(`/articles/${slug}`).then((r) => r.data),
    retry: false,
  });
  usePageTitle(a?.title);
  const [speaking, setSpeaking] = useState(false);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, [slug]);

  if (isLoading) return <p className="py-24 text-center text-brand-muted">Loading…</p>;
  if (isError || !a)
    return (
      <div className="py-24 text-center">
        <p className="text-brand-muted">Article not found.</p>
        <Link to="/articles" className="mt-4 inline-block text-brand underline">Back to articles</Link>
      </div>
    );

  const url = window.location.href;
  const share = (net) => {
    const enc = encodeURIComponent(url);
    const t = encodeURIComponent(a.title);
    const map = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc}`,
      twitter: `https://twitter.com/intent/tweet?url=${enc}&text=${t}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc}`,
      whatsapp: `https://wa.me/?text=${t}%20${enc}`,
    };
    if (net === "copy") {
      navigator.clipboard.writeText(url);
      toast.success("Link copied");
      return;
    }
    if (net === "print") {
      window.print();
      return;
    }
    window.open(map[net], "_blank");
  };

  const listen = () => {
    const synth = window.speechSynthesis;
    if (!synth) {
      toast.error("Text-to-speech is not supported in this browser");
      return;
    }
    if (speaking) {
      synth.cancel();
      setSpeaking(false);
      return;
    }
    const text = [a.title, a.subtitle, ...(a.body || []).map((b) => b.text)].filter(Boolean).join(". ");
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1;
    u.pitch = 1;
    u.onend = () => setSpeaking(false);
    u.onerror = () => {
      setSpeaking(false);
      toast.error("Listen isn't available in this browser");
    };
    synth.cancel();
    synth.speak(u);
    setSpeaking(true);
  };

  return (
    <div className="mx-auto max-w-6xl px-5 sm:px-8 pt-10 lg:flex lg:gap-10">
      {a.slug !== GUIDE_SLUG && (
        <aside data-testid="featured-guide-card" className="order-last mt-10 lg:order-first lg:mt-0 lg:w-64 lg:shrink-0">
          <div className="lg:sticky lg:top-24 rounded-xl border border-brand-line bg-brand-mist/50 p-4">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-brand-clay">Featured guide</span>
            <Link to={`/articles/${GUIDE_SLUG}`} data-testid="featured-guide-link" className="group mt-2 block">
              <div className="aspect-video overflow-hidden rounded-lg">
                <img src={GUIDE_IMAGE} alt="Kerala travel guide" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <h4 className="mt-2 font-heading text-base leading-snug text-brand-ink group-hover:text-brand">{GUIDE_TITLE}</h4>
              <span className="mt-1 inline-block text-xs font-medium text-brand">Read the guide →</span>
            </Link>
          </div>
        </aside>
      )}
      <article className="min-w-0 flex-1 lg:max-w-3xl">
      <button data-testid="back-button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-widest text-brand-clay">{a.topic}</span>
        {a.tags?.map((t) => (
          <Link key={t} to={`/tags/${t}`} className="rounded-full bg-brand-mist px-2.5 py-1 text-xs text-brand-muted hover:text-brand">
            #{t}
          </Link>
        ))}
      </div>

      <h1 className="mt-4 font-heading text-4xl sm:text-5xl leading-[1.1] text-brand-ink tracking-tight">{a.title}</h1>
      {a.subtitle && <p className="mt-4 text-xl text-brand-muted leading-relaxed">{a.subtitle}</p>}

      <div className="mt-6 flex flex-wrap items-center gap-4">
        {a.author && (
          <Link to={`/authors/${a.author.slug}`} data-testid="article-author" className="flex items-center gap-3">
            <img src={a.author.image} alt="" className="h-11 w-11 rounded-full object-cover" />
            <div>
              <p className="text-sm font-semibold text-brand-ink">{a.author.name}</p>
              <p className="text-xs text-brand-muted">{a.author.role}</p>
            </div>
          </Link>
        )}
        <div className="flex items-center gap-1.5 text-sm text-brand-muted">
          <Calendar className="h-4 w-4" /> {fmt(a.published_at)}
        </div>
      </div>

      {a.reviewed && (
        <div className="mt-5">
          <MedicalBadge reviewer={a.reviewer?.name} date={a.reviewed_at ? fmt(a.reviewed_at) : null} />
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="mt-8 overflow-hidden rounded-2xl border border-brand-line"
      >
        <img src={a.feature_image} alt={a.feature_alt} className="w-full object-cover" />
      </motion.div>

      {/* Share + Listen bar */}
      <div className="mt-6 flex flex-wrap items-center gap-2 border-y border-brand-line py-3">
        <button
          data-testid="listen-btn"
          onClick={listen}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
            speaking ? "bg-brand-clay text-white" : "bg-brand text-brand-sand hover:opacity-90"
          }`}
        >
          {speaking ? <Square className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          {speaking ? "Stop" : "Listen"}
        </button>
        <span className="ml-2 mr-1 text-xs font-medium uppercase tracking-wide text-brand-muted">Share</span>
        {[
          ["whatsapp", MessageCircle],
          ["facebook", Facebook],
          ["twitter", Twitter],
          ["linkedin", Linkedin],
          ["copy", Link2],
          ["print", Printer],
        ].map(([net, Icon]) => (
          <button
            key={net}
            data-testid={`share-${net}`}
            onClick={() => share(net)}
            className="grid h-9 w-9 place-items-center rounded-full border border-brand-line text-brand-muted transition-colors hover:border-brand hover:text-brand"
          >
            <Icon className="h-4 w-4" />
          </button>
        ))}
        <button
          data-testid="share-qr"
          onClick={() => setShowQR((v) => !v)}
          className="grid h-9 w-9 place-items-center rounded-full border border-brand-line text-brand-muted transition-colors hover:border-brand hover:text-brand"
        >
          <QrCode className="h-4 w-4" />
        </button>
      </div>

      {showQR && (
        <div data-testid="qr-panel" className="mt-4 flex flex-col items-center gap-3 rounded-xl border border-brand-line bg-white p-6">
          <QRCodeCanvas value={url} size={148} fgColor="#1B4332" bgColor="#ffffff" />
          <p className="text-xs text-brand-muted">Scan to open this article on your phone</p>
        </div>
      )}

      <div className="article-body mt-8" data-testid="article-body">
        {a.body?.map((block, i) =>
          block.type === "heading" ? (
            <h2 key={i} className="mt-8 mb-2 font-heading text-2xl text-brand-ink">{block.text}</h2>
          ) : block.type === "quote" ? (
            <blockquote key={i} className="my-4 border-l-4 border-brand-sage pl-4 italic text-brand-muted">{block.text}</blockquote>
          ) : (
            <p key={i}>{block.text}</p>
          )
        )}
      </div>

      {a.traveller_tips && (
        <div data-testid="traveller-tips" className="mt-10 rounded-2xl border border-brand-sage/30 bg-brand-sage/5 p-6">
          <div className="flex items-center gap-2 text-brand">
            <Compass className="h-5 w-5 text-brand-sage" />
            <h3 className="font-heading text-xl">{a.traveller_tips.heading}</h3>
          </div>
          {a.traveller_tips.subheading && (
            <p className="mt-1 text-sm font-medium text-brand-muted">{a.traveller_tips.subheading}</p>
          )}
          <ul className="mt-4 space-y-3">
            {a.traveller_tips.tips?.map((t, i) => (
              <li key={i} className="flex gap-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-sage" />
                <span className="text-sm leading-relaxed text-brand-ink/85">
                  <span className="font-semibold text-brand-ink">{t.title}: </span>{t.text}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <SafetyNotice warnings={a.warnings} />

      {a.references?.length > 0 && (
        <div className="mt-10 rounded-xl bg-brand-mist p-5">
          <h3 className="font-heading text-lg text-brand-ink">References</h3>
          <ul className="mt-3 space-y-1.5 text-sm text-brand-muted">
            {a.references.map((r, i) => (
              <li key={i}>• {r}</li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-8 rounded-lg border border-brand-line bg-white p-4 text-xs text-brand-muted">
        <strong>Medical disclaimer:</strong> This content is for education only and is not a substitute for
        professional medical advice, diagnosis or treatment. Always seek the advice of a qualified practitioner.
      </p>
      </article>
    </div>
  );
}
